// ─── GOOGLE CALENDAR REST API ─────────────────────────────────────────────────
// Browser fetch() calls to GCal API v3. Token comes from OAuth sign-in.
// All functions return null on auth error (caller should prompt re-auth).

import { getGCalToken, GCalReconsentError } from './firebase';

const BASE = 'https://www.googleapis.com/calendar/v3';
const FETCH_TIMEOUT_MS = 10_000;

// Thrown when a real interactive reconnect (a click-triggered popup) is
// needed — callers should show a "Reconnect Google Calendar" affordance
// rather than retrying silently, which would just hang.
export class GCalAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = 'GCalAuthError';
  }
}

function timedFetch(url, options) {
  return fetch(url, { ...options, signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
}

// getGCalToken() is async under server-side auth (it may call the token
// function); on the legacy path it resolves immediately from localStorage.
// Awaiting covers both. A GCalReconsentError means the server has no usable
// grant left — same user-facing outcome as a 401, so it becomes GCalAuthError
// and the existing reconnect banner handles it unchanged.
async function resolveToken() {
  try {
    return await getGCalToken();
  } catch (err) {
    if (err instanceof GCalReconsentError) throw new GCalAuthError(err.message);
    throw err;
  }
}

async function gcalFetch(url, options = {}) {
  const token = await resolveToken();
  if (!token) throw new GCalAuthError('Google Calendar needs to be reconnected');

  let res;
  try {
    res = await timedFetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  } catch (err) {
    throw new Error(err?.name === 'TimeoutError' ? 'GCal request timed out' : err.message);
  }

  if (res.status === 401) throw new GCalAuthError('Google Calendar needs to be reconnected');
  if (!res.ok) throw new Error(`GCal API error: ${res.status} ${res.statusText}`);
  return res.json();
}

// ─── FETCH EVENTS ─────────────────────────────────────────────────────────────

const MAX_PAGES = 10; // safety ceiling — 10 * maxResults per calendar is plenty for any real family calendar

// Follows nextPageToken so a busy calendar's later events (Google returns
// earliest-first) aren't silently truncated at a single page's maxResults.
export async function fetchEvents({ calendarId = 'primary', timeMin, timeMax, maxResults = 250 }) {
  const items = [];
  let pageToken;
  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({
      singleEvents: 'true',
      orderBy: 'startTime',
      maxResults: String(maxResults),
      ...(timeMin ? { timeMin } : {}),
      ...(timeMax ? { timeMax } : {}),
      ...(pageToken ? { pageToken } : {}),
    });
    const data = await gcalFetch(`${BASE}/calendars/${encodeURIComponent(calendarId)}/events?${params}`);
    items.push(...(data.items || []));
    if (!data.nextPageToken) break;
    pageToken = data.nextPageToken;
  }
  return items;
}

// ─── PATCH EVENT ──────────────────────────────────────────────────────────────

export async function patchEvent(calendarId, eventId, body) {
  return gcalFetch(
    `${BASE}/calendars/${encodeURIComponent(calendarId)}/events/${eventId}`,
    { method: 'PATCH', body: JSON.stringify(body) }
  );
}

// ─── CREATE EVENT ─────────────────────────────────────────────────────────────

export async function createEvent(calendarId, body, { sendUpdates } = {}) {
  const qs = sendUpdates ? `?sendUpdates=${sendUpdates}` : '';
  return gcalFetch(
    `${BASE}/calendars/${encodeURIComponent(calendarId)}/events${qs}`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// ─── DELETE EVENT ─────────────────────────────────────────────────────────────

export async function deleteEvent(calendarId, eventId) {
  const token = await resolveToken();
  if (!token) throw new GCalAuthError('Google Calendar needs to be reconnected');

  let res;
  try {
    res = await timedFetch(
      `${BASE}/calendars/${encodeURIComponent(calendarId)}/events/${eventId}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (err) {
    throw new Error(err?.name === 'TimeoutError' ? 'GCal request timed out' : err.message);
  }

  if (res.status === 204 || res.status === 200) return true;
  if (res.status === 401) throw new GCalAuthError('Google Calendar needs to be reconnected');
  throw new Error(`Delete failed: ${res.status}`);
}

// ─── LIST CALENDARS ───────────────────────────────────────────────────────────

export async function listCalendars() {
  const data = await gcalFetch(`${BASE}/users/me/calendarList?maxResults=100`);
  return data.items || [];
}

// ─── BATCH PATCH (for Lock In) ────────────────────────────────────────────────
// GCal batch API requires multipart — we just do sequential fetches since
// Lock In is a low-frequency operation (family of 4–5 events max per session).

export async function batchPatchEvents(patches) {
  // patches: [{ calendarId, eventId, body }]
  const results = [];
  for (const p of patches) {
    try {
      const r = await patchEvent(p.calendarId, p.eventId, p.body);
      results.push({ ok: true, eventId: p.eventId, result: r });
    } catch (err) {
      results.push({ ok: false, eventId: p.eventId, error: err.message });
    }
  }
  return results;
}
